import React from "react";
import { Typography, Flex, Spin, Button, Empty } from "antd";
import {
  Search01Icon,
  Clock01Icon,
  Cancel01Icon,
  UserIcon,
  File01Icon,
  BarChartIcon,
  UserGroupIcon,
  ArrowRight01Icon,
  RupeeIcon,
} from "hugeicons-react";
import "./globalSearchDropdown.css";

const { Text } = Typography;

const isMac = typeof window !== "undefined" && /(Mac|iPhone|iPod|iPad)/i.test(navigator?.userAgentData?.platform || navigator?.platform || navigator?.userAgent || "");
const SEARCH_SHORTCUT_LABEL = isMac ? "⌘K" : "Ctrl+K";

const BADGE_TEXT_COLORS = {
  purple: "#7c3aed",
  blue: "#2563eb",
  green: "#16a34a",
  volcano: "#ea580c",
  cyan: "#0891b2",
  orange: "#d97706",
};

const CATEGORY_ICONS = {
  lead: <UserIcon size={18} color="#0f172a" />,
  pipeline: <BarChartIcon size={18} color="#0f172a" />,
  invoice: <RupeeIcon size={18} color="#0f172a" />,
  proposal: <File01Icon size={18} color="#0f172a" />,
  employee: <UserGroupIcon size={18} color="#0f172a" />,
};

function GlobalSearchDropdown({ isOpen, loading, query, suggestions = [], selectedIndex = -1, recentSearches = [], onSelectSuggestion, onSelectRecentSearch, onRemoveRecentSearch, onClearAllRecentSearches }) {
  if (!isOpen) return null;

  const hasQuery = Boolean(query?.trim());
  const hasRecent = Boolean(recentSearches?.length);
  const hasSuggestions = Boolean(suggestions?.length);

  return (
    <div className="global-search-dropdown">
      {/* ── 1. ZERO-STATE: RECENT SEARCHES ── */}
      {!hasQuery && (
        <div className="gs-recent-section">
          {hasRecent ? (
            <>
              <Flex justify="space-between" align="center" className="gs-section-header">
                <Flex align="center" gap={6}>
                  <Clock01Icon size={14} color="#64748b" />
                  <Text strong style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Recent Searches
                  </Text>
                </Flex>
                <Button type="link" size="small" onClick={onClearAllRecentSearches} style={{ fontSize: "0.75rem", padding: 0, color: "#94a3b8" }}>
                  Clear All
                </Button>
              </Flex>

              <div className="gs-recent-list">
                {recentSearches.map((term, i) => (
                  <div key={`recent-${term}-${i}`} className="gs-recent-item">
                    <button
                      type="button"
                      onClick={() => onSelectRecentSearch(term)}
                      style={{
                        background: "none",
                        border: "none",
                        display: "flex",
                        flex: 1,
                        alignItems: "center",
                        gap: 8,
                        textAlign: "left",
                        cursor: "pointer",
                        padding: 0,
                        fontFamily: "inherit",
                        minWidth: 0,
                      }}
                    >
                      <Search01Icon size={14} color="#94a3b8" />
                      <Text ellipsis style={{ fontSize: "0.85rem", color: "#1e293b" }}>
                        {term}
                      </Text>
                    </button>
                    <Button
                      type="text"
                      size="small"
                      icon={<Cancel01Icon size={12} color="#94a3b8" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveRecentSearch(term);
                      }}
                      className="gs-recent-del-btn"
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="gs-empty-hint">
              <Text type="secondary" style={{ fontSize: "0.82rem" }}>
                Type to search across <Text strong>Leads</Text>, <Text strong>Deals</Text>, <Text strong>Invoices</Text>, and <Text strong>Proposals</Text>.
              </Text>
            </div>
          )}

          <div className="gs-footer-shortcut">
            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
              Tip: Press <kbd className="gs-kbd">{SEARCH_SHORTCUT_LABEL}</kbd> to focus search from anywhere
            </Text>
          </div>
        </div>
      )}

      {/* ── 2. LOADING STATE ── */}
      {hasQuery && loading && (
        <Flex justify="center" align="center" gap={10} style={{ padding: "1.5rem 1rem" }}>
          <Spin size="small" />
          <Text type="secondary" style={{ fontSize: "0.82rem" }}>
            Searching...
          </Text>
        </Flex>
      )}

      {/* ── 3. LIVE MULTI-ENTITY RESULTS ── */}
      {hasQuery && !loading && hasSuggestions && (
        <div className="gs-results-section">
          <div className="gs-section-header">
            <Text strong style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Matching Records ({suggestions.length})
            </Text>
          </div>

          <div className="gs-results-list">
            {suggestions.map((item, index) => {
              const isSelected = selectedIndex === index;
              return (
                <button
                  type="button"
                  key={item.id || `sugg-${item.title}-${index}`}
                  className={`gs-result-item ${isSelected ? "selected" : ""}`}
                  onClick={() => onSelectSuggestion(item)}
                  style={{
                    width: "100%",
                    border: "none",
                    background: "none",
                    textAlign: "left",
                    fontFamily: "inherit",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div className="gs-result-icon">{CATEGORY_ICONS[item.category] || <Search01Icon size={18} color="#0f172a" />}</div>

                  <div className="gs-result-content">
                    <Text strong className="gs-result-title" ellipsis>
                      {item.title}
                    </Text>
                    {item.subtitle && (
                      <Text type="secondary" className="gs-result-subtitle" ellipsis>
                        {item.subtitle}
                      </Text>
                    )}
                  </div>

                  <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
                    {item.badge && (
                      <span
                        className="gs-result-badge"
                        style={{
                          color: BADGE_TEXT_COLORS[item.badgeColor] || item.badgeColor || "#64748b",
                        }}
                      >
                        {item.badge}
                      </span>
                    )}
                    <ArrowRight01Icon size={14} className="gs-result-arrow" />
                  </Flex>
                </button>
              );
            })}
          </div>

          <div className="gs-footer-shortcut">
            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
              Press <kbd className="gs-kbd">↵ Enter</kbd> to search in current view • <kbd className="gs-kbd">↑↓</kbd> to navigate
            </Text>
          </div>
        </div>
      )}

      {/* ── 4. EMPTY RESULTS ── */}
      {hasQuery && !loading && !hasSuggestions && (
        <div className="gs-no-results">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Flex vertical align="center" gap={4}>
                <Text style={{ fontSize: "0.85rem", color: "#475569" }}>
                  No direct matches for <Text strong>"{query}"</Text>
                </Text>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Press Enter to perform a full search in the current workspace.
                </Text>
              </Flex>
            }
          />
        </div>
      )}
    </div>
  );
}

export default GlobalSearchDropdown;
